import { useApplicantRelatedAccountsQuery } from '@/components/shared/api/queries/applicant';

import { Applicant } from '../Applicant.types';
import { getDeltaFundingMethodLinkStatusDisplay } from '../Applicant.utils';
import { RELATED_ACCOUNTS_SOURCES } from './ApplicantRelatedAccounts/constants';
import { useTabNavigation } from '../hooks/useTabNavigation';
import { ApplicantInfoCard, ApplicantInfoRow } from './ApplicantInfoCard';

const LOADPAY_PREFERRED_CHECKBOX = 'LegalAuthorizeLoadPayAsPreferredForAllFuturePayments';
const DELTA_LINK_STATUS_LABEL = 'Automatic Delta Funding Method Link Status';
const OPTED_OUT_LINK_STATUS = 'Automations are disabled due to Opt-Out';

interface LoadPayAccountRowsProps {
    applicantId: string;
    isOptedIn: boolean | null;
}

// Live account data, so only the current application shows it; a submission holds none of it.
const LoadPayAccountRows = ({ applicantId, isOptedIn }: LoadPayAccountRowsProps) => {
    const { navigateToTab } = useTabNavigation();
    const {
        data: relatedAccounts,
        isLoading: isRelatedAccountsLoading,
        isLoadingError: isRelatedAccountsLoadingError,
    } = useApplicantRelatedAccountsQuery({ applicantId });

    const directlyLinkedAccount = relatedAccounts?.loadPayAccounts?.find((a) => a.isDirectlyLinked);
    const suspectedCount = relatedAccounts?.loadPayAccounts?.filter((a) => !a.isDirectlyLinked).length ?? 0;
    const isLoadPayUnavailable =
        isRelatedAccountsLoadingError ||
        (relatedAccounts?.unavailableSources?.includes(RELATED_ACCOUNTS_SOURCES.LOAD_PAY) ?? false);

    const renderLinkStatus = () => {
        if (isRelatedAccountsLoading) {
            return 'Loading...';
        }

        if (isLoadPayUnavailable) {
            return '⚠ Could not load LoadPay accounts';
        }

        if (isOptedIn === false) {
            return OPTED_OUT_LINK_STATUS;
        }

        if (directlyLinkedAccount?.automaticDeltaFundingMethodLinkState) {
            return getDeltaFundingMethodLinkStatusDisplay(
                directlyLinkedAccount.automaticDeltaFundingMethodLinkState,
                directlyLinkedAccount.deltaClientId,
            );
        }

        return '⏳ Awaiting Related Account data';
    };

    return (
        <>
            <ApplicantInfoRow
                label={DELTA_LINK_STATUS_LABEL}
                value={renderLinkStatus()}
                isLive
                data-testid="loadpay-delta-link-status"
            />
            {!isRelatedAccountsLoading && suspectedCount > 0 ? (
                <ApplicantInfoRow
                    label="Suspected Relationships"
                    value={
                        <span>
                            {suspectedCount} other LoadPay{' '}
                            {suspectedCount === 1 ? 'account' : 'accounts'} with a Suspected
                            Relationship found.{' '}
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigateToTab('tab-related-accounts');
                                }}
                                data-testid="loadpay-related-accounts-link"
                            >
                                View in Related Entities →
                            </a>
                        </span>
                    }
                    isLive
                    data-testid="loadpay-suspected-relationships"
                />
            ) : null}
        </>
    );
};

interface LoadPayInformationProps {
    applicant: Applicant;
    isSubmission?: boolean;
}

export const LoadPayInformation = ({ applicant, isSubmission = false }: LoadPayInformationProps) => {
    if (!applicant.loadPayProduct) {
        return null;
    }

    const preferredPaymentCheckbox = applicant.legalCheckboxes?.find(
        (c) => c.nameOfCheckbox === LOADPAY_PREFERRED_CHECKBOX,
    );
    const isOptedIn = preferredPaymentCheckbox?.isChecked ?? null;

    return (
        <ApplicantInfoCard title="LoadPay Information">
            <ApplicantInfoRow
                label="Opted for LoadPay as Preferred Payment Method"
                value={isOptedIn === null ? 'Awaiting Applicant Selection' : String(isOptedIn).toUpperCase()}
                data-testid="loadpay-preferred-payment"
            />
            {isSubmission ? (
                // The submission holds no link status; only its own opt-out says anything about it.
                <ApplicantInfoRow
                    label={DELTA_LINK_STATUS_LABEL}
                    value={isOptedIn === false ? OPTED_OUT_LINK_STATUS : undefined}
                    data-testid="loadpay-delta-link-status"
                />
            ) : (
                <LoadPayAccountRows applicantId={applicant.id} isOptedIn={isOptedIn} />
            )}
        </ApplicantInfoCard>
    );
};
